// CalorieAI Service - 跨平台數據管理服務 (使用 Dexie.js)
import { v4 as uuidv4 } from 'uuid';
import { db } from './db';

class CalorieAIService {
  constructor() {
    // 將初始化動作改為異步非阻塞，避免卡住主執行緒
    this.initPromise = this.lazyInitialize();
  }

  async lazyInitialize() {
    // 延遲執行初始化，讓 UI 先渲染
    await new Promise(resolve => setTimeout(resolve, 50));
    
    // 初始化預設食物資料庫 (非阻塞)
    this.initializeFoodDatabase().catch(e => console.error(e));
  }

  /**
   * 生成唯一 ID
   */
  generateId() {
    return uuidv4();
  }

  // ==================== 食物記錄管理 ====================

  /**
   * 添加食物記錄
   */
  async addFoodRecord(foodData) {
    try {
      const recordedAt = foodData.recorded_at || new Date().toISOString();
      const date = new Date(recordedAt).toISOString().split('T')[0];

      const newRecord = {
        id: this.generateId(),
        food_name: foodData.food_name,
        quantity: foodData.quantity || 1,
        unit: foodData.unit || '份',
        calories: Math.round(foodData.calories || 0),
        protein: parseFloat((foodData.protein || 0).toFixed(1)),
        carbs: parseFloat((foodData.carbs || 0).toFixed(1)),
        fat: parseFloat((foodData.fat || 0).toFixed(1)),
        meal_type: foodData.meal_type || 'other',
        recorded_at: recordedAt,
        date: date, // 用於快速按日期查詢
        created_at: new Date().toISOString(),
        completion_percentage: foodData.completion_percentage || 100
      };

      // 確保不保存 image_url 避免儲存空間過大
      if (newRecord.image_url) delete newRecord.image_url;

      await db.foodRecords.add(newRecord);
      return newRecord;
    } catch (error) {
      console.error('❌ 添加食物記錄失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取所有食物記錄
   */
  async getAllFoodRecords() {
    return await db.foodRecords.toArray();
  }

  // 為了向下兼容，提供 getFoodRecords 別名
  async getFoodRecords() {
    return this.getAllFoodRecords();
  }

  /**
   * 根據日期獲取食物記錄
   */
  async getFoodRecordsByDate(date) {
    try {
      return await db.foodRecords.where('date').equals(date).toArray();
    } catch (error) {
      console.error('獲取日期食物記錄失敗:', error);
      return [];
    }
  }

  /**
   * 根據日期範圍獲取食物記錄
   */
  async getFoodRecordsByDateRange(startDate, endDate) {
    try {
      return await db.foodRecords.where('date').between(startDate, endDate, true, true).toArray();
    } catch (error) {
      console.error('獲取日期範圍食物記錄失敗:', error);
      return [];
    }
  }

  /**
   * 更新食物記錄
   */
  async updateFoodRecord(id, updateData) {
    try {
      if (updateData.recorded_at) {
        updateData.date = new Date(updateData.recorded_at).toISOString().split('T')[0];
      }
      if (updateData.image_url) delete updateData.image_url;

      await db.foodRecords.update(id, updateData);
      const updatedRecord = await db.foodRecords.get(id);
      if (!updatedRecord) {
        throw new Error('找不到指定的食物記錄');
      }
      return updatedRecord;
    } catch (error) {
      console.error('更新食物記錄失敗:', error);
      throw error;
    }
  }

  /**
   * 刪除食物記錄
   */
  async deleteFoodRecord(id) {
    try {
      await db.foodRecords.delete(id);
      return true;
    } catch (error) {
      console.error('刪除食物記錄失敗:', error);
      throw error;
    }
  }

  // ==================== 卡路里目標管理 ====================

  /**
   * 設定卡路里目標
   */
  async setCalorieGoals(goalsData) {
    try {
      // 將所有現有目標設為非活躍
      await db.goals.where('is_active').equals(1).modify({ is_active: 0 });

      const newGoal = {
        id: this.generateId(),
        daily_calories: goalsData.daily_calories || 2000,
        daily_protein: goalsData.daily_protein || 150,
        daily_carbs: goalsData.daily_carbs || 250,
        daily_fat: goalsData.daily_fat || 67,
        weight_goal: goalsData.weight_goal || 'maintain',
        activity_level: goalsData.activity_level || 'moderate',
        target_weight: goalsData.target_weight || 70,
        current_weight: goalsData.current_weight || 70,
        height: goalsData.height || 170,
        age: goalsData.age || 25,
        gender: goalsData.gender || 'male',
        is_active: 1, // Dexie does not support boolean index properly in all browsers, so 1/0 is safer
        created_at: new Date().toISOString()
      };

      await db.goals.add(newGoal);
      return newGoal;
    } catch (error) {
      console.error('❌ 設定卡路里目標失敗:', error);
      throw error;
    }
  }

  /**
   * 更新卡路里目標
   */
  async updateCalorieGoals(goalsData) {
    try {
      const activeGoal = await this.getActiveGoals();
      
      if (activeGoal) {
        const updatedGoal = {
          ...goalsData,
          updated_at: new Date().toISOString()
        };
        await db.goals.update(activeGoal.id, updatedGoal);
        return await db.goals.get(activeGoal.id);
      } else {
        const newGoal = {
          id: this.generateId(),
          ...goalsData,
          is_active: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await db.goals.add(newGoal);
        return newGoal;
      }
    } catch (error) {
      console.error('❌ 更新目標設定失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取活躍的卡路里目標
   */
  async getActiveGoals() {
    try {
      const goals = await db.goals.where('is_active').equals(1).toArray();
      // If none found with 1, it might be saved as boolean true by old imports, so check true as well just in case.
      if (goals.length > 0) return goals[0];

      // Fallback if imported with boolean
      const booleanGoals = await db.goals.filter(g => g.is_active === true).toArray();
      return booleanGoals.length > 0 ? booleanGoals[0] : null;
    } catch (error) {
      console.error('❌ 獲取活躍目標失敗:', error);
      return null;
    }
  }

  /**
   * 獲取所有卡路里目標
   */
  async getAllGoals() {
    return await db.goals.toArray();
  }

  // ==================== 食物資料庫管理 ====================

  /**
   * 初始化預設食物資料庫
   */
  async initializeFoodDatabase() {
    try {
      const count = await db.foodDatabase.count();
      if (count === 0) {
        const defaultDatabase = [
          // 水果類
          { id: 'f1', name: '蘋果', unit: '顆', calories: 95, protein: 0.5, carbs: 25, fat: 0.3, defaultQuantity: 1, category: '水果' },
          { id: 'f2', name: '香蕉', unit: '顆', calories: 105, protein: 1.3, carbs: 27, fat: 0.4, defaultQuantity: 1, category: '水果' },
          // 可以根據需要添加更多預設數據
        ];
        
        await db.foodDatabase.bulkAdd(defaultDatabase);
      }
    } catch (error) {
      console.error('初始化食物資料庫失敗:', error);
    }
  }

  /**
   * 搜尋食物資料庫
   */
  async searchFoodDatabase(query) {
    try {
      const dbFoods = await db.foodDatabase.toArray();
      const customFoods = await db.customFoods.toArray();
      
      const allFoods = [...dbFoods, ...customFoods];
      
      if (!query) return allFoods;

      const lowerQuery = query.toLowerCase();
      return allFoods.filter(food => 
        (food.name && food.name.toLowerCase().includes(lowerQuery)) ||
        (food.name_en && food.name_en.toLowerCase().includes(lowerQuery))
      );
    } catch (error) {
      console.error('搜尋食物資料庫失敗:', error);
      return [];
    }
  }

  /**
   * 根據分類獲取食物
   */
  async getFoodsByCategory(category) {
    try {
      return await db.foodDatabase.where('category').equals(category).toArray();
    } catch (error) {
      console.error('根據分類獲取食物失敗:', error);
      return [];
    }
  }

  /**
   * 獲取所有食物分類
   */
  async getFoodCategories() {
    try {
      const foods = await db.foodDatabase.toArray();
      const categories = [...new Set(foods.map(food => food.category).filter(Boolean))];
      return categories;
    } catch (error) {
      console.error('獲取食物分類失敗:', error);
      return [];
    }
  }

  // ==================== 自定義食物管理 ====================

  /**
   * 添加自定義食物
   */
  async addCustomFood(foodData) {
    try {
      const newFood = {
        id: this.generateId(),
        name: foodData.name,
        calories_per_unit: foodData.calories_per_unit || 0,
        unit: foodData.unit || '份',
        protein: foodData.protein || 0,
        carbs: foodData.carbs || 0,
        fat: foodData.fat || 0,
        category: foodData.category || '自定義',
        created_at: new Date().toISOString()
      };

      await db.customFoods.add(newFood);
      return newFood;
    } catch (error) {
      console.error('添加自定義食物失敗:', error);
      throw error;
    }
  }

  /**
   * 獲取所有自定義食物
   */
  async getCustomFoods() {
    return await db.customFoods.toArray();
  }

  /**
   * 刪除自定義食物
   */
  async deleteCustomFood(id) {
    try {
      await db.customFoods.delete(id);
      return true;
    } catch (error) {
      console.error('刪除自定義食物失敗:', error);
      throw error;
    }
  }

  // ==================== 統計分析 ====================

  /**
   * 獲取日期範圍內的統計數據
   */
  async getStatistics(startDate, endDate) {
    try {
      const records = await this.getFoodRecordsByDateRange(startDate, endDate);
      
      const stats = {
        totalCalories: 0,
        totalProtein: 0,
        totalCarbs: 0,
        totalFat: 0,
        recordCount: records.length,
        dailyAverages: {},
        mealTypeBreakdown: {}
      };

      records.forEach(record => {
        stats.totalCalories += record.calories;
        stats.totalProtein += record.protein;
        stats.totalCarbs += record.carbs;
        stats.totalFat += record.fat;

        if (!stats.mealTypeBreakdown[record.meal_type]) {
          stats.mealTypeBreakdown[record.meal_type] = {
            calories: 0,
            count: 0
          };
        }
        stats.mealTypeBreakdown[record.meal_type].calories += record.calories;
        stats.mealTypeBreakdown[record.meal_type].count += 1;
      });

      const days = Math.max(1, Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24)) + 1);
      stats.dailyAverages = {
        calories: stats.totalCalories / days,
        protein: stats.totalProtein / days,
        carbs: stats.totalCarbs / days,
        fat: stats.totalFat / days
      };

      return stats;
    } catch (error) {
      console.error('獲取統計數據失敗:', error);
      throw error;
    }
  }

  // ==================== 數據匯出/匯入 ====================

  /**
   * 匯出所有數據 (透過 dexie-export-import)
   */
  async exportAllData() {
    try {
      // 這裡直接返回 blob 給調用方處理
      const blob = await db.export({ prettyJson: true });
      // 將 blob 轉成字串以維持相容性
      return await blob.text();
    } catch (error) {
      console.error('匯出數據失敗:', error);
      throw error;
    }
  }

  /**
   * 匯入數據
   */
  async importData(jsonString) {
    try {
      const blob = new Blob([jsonString], { type: "application/json" });
      await db.import(blob, {
        overwriteValues: true,
        clearTablesBeforeImport: true
      });
      return true;
    } catch (error) {
      console.error('匯入數據失敗:', error);
      // Fallback: 如果不是標準 Dexie 匯出格式，嘗試解析為舊的 JSON 格式
      return await this.importLegacyData(jsonString);
    }
  }

  async importLegacyData(jsonString) {
    try {
      const data = JSON.parse(jsonString);
      
      if (data.foodRecords && Array.isArray(data.foodRecords)) {
        await db.foodRecords.clear();
        const processedRecords = data.foodRecords.map(record => ({
          ...record,
          date: new Date(record.recorded_at).toISOString().split('T')[0]
        }));
        await db.foodRecords.bulkAdd(processedRecords);
      }
      if (data.goals && Array.isArray(data.goals)) {
        await db.goals.clear();
        await db.goals.bulkAdd(data.goals);
      }
      if (data.customFoods && Array.isArray(data.customFoods)) {
        await db.customFoods.clear();
        await db.customFoods.bulkAdd(data.customFoods);
      }

      return true;
    } catch (error) {
      console.error('舊格式匯入失敗:', error);
      throw error;
    }
  }

  /**
   * 清除所有數據
   */
  async clearAllData() {
    try {
      await Promise.all([
        db.foodRecords.clear(),
        db.goals.clear(),
        db.customFoods.clear(),
        db.weightRecords.clear()
      ]);
      
      this.initializeFoodDatabase();
      return true;
    } catch (error) {
      console.error('清除數據失敗:', error);
      throw error;
    }
  }

  // ==================== 進度追蹤相關方法 ====================

  /**
   * 獲取今日統計數據
   */
  async getTodayStats() {
    try {
      const today = new Date().toISOString().split('T')[0];
      const todayRecords = await this.getFoodRecordsByDate(today);
      
      const stats = {
        consumed_calories: 0,
        consumed_protein: 0,
        consumed_carbs: 0,
        consumed_fat: 0,
        meal_count: todayRecords.length
      };

      todayRecords.forEach(record => {
        stats.consumed_calories += record.calories || 0;
        stats.consumed_protein += record.protein || 0;
        stats.consumed_carbs += record.carbs || 0;
        stats.consumed_fat += record.fat || 0;
      });

      return stats;
    } catch (error) {
      console.error('獲取今日統計失敗:', error);
      return {
        consumed_calories: 0,
        consumed_protein: 0,
        consumed_carbs: 0,
        consumed_fat: 0,
        meal_count: 0
      };
    }
  }

  /**
   * 獲取本週統計數據（7天）
   */
  async getWeeklyStats() {
    try {
      const weeklyData = [];
      const today = new Date();
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(today);
        const dayOfWeek = today.getDay(); // 0 = 週日, 1 = 週一, ...
        const daysFromSunday = (dayOfWeek + 7 - i) % 7;
        date.setDate(today.getDate() - daysFromSunday);
        
        const dateString = date.toISOString().split('T')[0];
        const dayRecords = await this.getFoodRecordsByDate(dateString);
        
        const dayCalories = dayRecords.reduce((sum, record) => sum + (record.calories || 0), 0);
        weeklyData.push({
          date: dateString,
          calories: dayCalories
        });
      }

      return weeklyData;
    } catch (error) {
      console.error('獲取週統計失敗:', error);
      return Array(7).fill({ calories: 0 });
    }
  }

  /**
   * 獲取體重記錄歷史
   */
  async getWeightHistory() {
    try {
      const weights = await db.weightRecords.orderBy('recorded_at').toArray();
      return weights;
    } catch (error) {
      console.error('獲取體重歷史失敗:', error);
      return [];
    }
  }

  /**
   * 添加體重記錄
   */
  async addWeightRecord(weight) {
    try {
      const newRecord = {
        id: this.generateId(),
        weight: parseFloat(weight),
        recorded_at: new Date().toISOString(),
        created_at: new Date().toISOString()
      };

      await db.weightRecords.add(newRecord);
      return newRecord;
    } catch (error) {
      console.error('添加體重記錄失敗:', error);
      throw error;
    }
  }
}

// 創建單例實例
export const calorieAIService = new CalorieAIService();
export default calorieAIService;
